import { remarksSchema } from './campaign';
import {
  bankAccountHolder,
  fullWidthCharacter,
  halfWidthNumber,
  nameKanaRegex,
  nameRomajiRegex,
  phoneNumberWithHyphenRequired,
  postalCode,
  fullWidthCharacterOnly,
} from './regex';

import {
  BankAccountTypeKey,
  ClearingCycleKey,
} from '@/types';
import {
  InferType,
  date,
  object,
  string,
} from '@/validations/custom-yup';

/** 外部企業ID */
export const externalCompanyIdSchema = string()
  .label('外部企業ID')
  .notRequired()
  .max(100)
  .when(
    'regex',
    ([regex], schema) => (regex ? schema.matches(regex, '外部企業IDのフォーマットを確認してください。') : schema),
  );

/** 企業名 */
export const companyNameSchema = string()
  .label('企業名')
  .required()
  .max(100)
  .matches(fullWidthCharacter, '企業名に使用できない文字が含まれています。');

/** 企業名（カナ） */
export const companyNameKanaSchema = string()
  .label('企業名（カナ）')
  .notRequired()
  .max(64)
  .matches(nameKanaRegex, ({ label }) => `${label}は全角カナ・英数字・一部記号で入力してください。`);

/** 企業名（ローマ字） */
export const companyNameRomajiSchema = string()
  .label('企業名（ローマ字）')
  .notRequired()
  .max(32)
  .matches(nameRomajiRegex, ({ label }) => `${label}は半角英数字・記号（「"」「,」を除く）で入力してください。`);

/** 業種コード */
export const companyMerchantCategoryCodeSchema = string()
  .label('業種コード')
  .notRequired()
  .max(5)
  .matches(halfWidthNumber, ({ label }) => `${label}は半角数字で入力してください。`);

/** 電話番号 */
export const phoneSchema = string()
  .label('電話番号')
  .required()
  .max(13)
  .matches(phoneNumberWithHyphenRequired, '電話番号を正しい形式で入力してください。');

/** 郵便番号 */
export const postalCodeSchema = string()
  .label('郵便番号')
  .required()
  .matches(postalCode, '郵便番号を正しい形式で入力してください。');

/** 都道府県 */
export const prefectureSchema = string()
  .label('都道府県')
  .required()
  .max(4)
  .matches(fullWidthCharacterOnly, '都道府県に使用できない文字が含まれています。');

/** 市区町村番地 */
export const municipalitiesSchema = string()
  .label('市区町村番地')
  .required()
  .max(100)
  .matches(fullWidthCharacter, '市区町村番地を全角で入力してください。');

/** 建物名 */
export const buildingNameSchema = string()
  .label('建物名')
  .max(100)
  .default('')
  .ensure()
  .matches(fullWidthCharacter, '建物名を全角で入力してください。');

/** 利用開始日 */
export const startsAtSchema = date()
  .label('利用開始日')
  .required()
  .transform((value, originalValue) => (originalValue instanceof Date ? value : undefined));

/** 利用終了日 */
export const endsAtSchema = date()
  .label('利用終了日')
  .notRequired()
  .transform((value, originalValue) => (originalValue instanceof Date ? value : undefined))
  .when(
    'startsAt',
    ([startsAt], schema) => (startsAt instanceof Date
      ? schema
        .test(
          'isMoreThanStartsAt',
          '利用終了日は利用開始日以降の日付を入力してください。',
          (value) => (value ? value.getTime() >= startsAt.getTime() : true),
        )
      : schema),
  );

/** 企業自由項目１ */
export const companyCustomField1Schema = string()
  .label('企業自由項目１')
  .notRequired()
  .max(100)
  .when(
    'companyCustomField1Regex',
    ([companyCustomField1Regex], schema) => (companyCustomField1Regex ? schema.matches(companyCustomField1Regex, '企業自由項目１のフォーマットを確認してください。') : schema),
  );

/** 企業自由項目２ */
export const companyCustomField2Schema = string()
  .label('企業自由項目２')
  .notRequired()
  .max(100)
  .when(
    'companyCustomField2Regex',
    ([companyCustomField2Regex], schema) => (companyCustomField2Regex ? schema.matches(companyCustomField2Regex, '企業自由項目２のフォーマットを確認してください。') : schema),
  );

/** 企業自由項目３ */
export const companyCustomField3Schema = string()
  .label('企業自由項目３')
  .notRequired()
  .max(100)
  .when(
    'companyCustomField3Regex',
    ([companyCustomField3Regex], schema) => (companyCustomField3Regex ? schema.matches(companyCustomField3Regex, '企業自由項目３のフォーマットを確認してください。') : schema),
  );

/** 企業自由項目４ */
export const companyCustomField4Schema = string()
  .label('企業自由項目４')
  .notRequired()
  .max(100)
  .when(
    'companyCustomField4Regex',
    ([companyCustomField4Regex], schema) => (companyCustomField4Regex ? schema.matches(companyCustomField4Regex, '企業自由項目４のフォーマットを確認してください。') : schema),
  );

/** 企業自由項目５ */
export const companyCustomField5Schema = string()
  .label('企業自由項目５')
  .notRequired()
  .max(100)
  .when(
    'companyCustomField5Regex',
    ([companyCustomField5Regex], schema) => (companyCustomField5Regex ? schema.matches(companyCustomField5Regex, '企業自由項目５のフォーマットを確認してください。') : schema),
  );

/** 金融機関コード */
export const bankCodeSchema = string()
  .label('金融機関コード')
  .length(4, '金融機関コードは4桁で入力してください。')
  .matches(halfWidthNumber, '金融機関コードは半角数字で入力してください。')
  .required();

/** 支店コード */
export const bankBranchCodeSchema = string()
  .label('支店コード')
  .length(3, '支店コードは3桁で入力してください。')
  .matches(halfWidthNumber, '支店コードは半角数字で入力してください。')
  .required();

/** 口座科目 */
export const bankAccountTypeSchema = string<BankAccountTypeKey>()
  .label('口座科目')
  .required(({ label }) => `${label}を選択してください。`);

/** 口座番号 */
export const bankAccountNumberSchema = string()
  .label('口座番号')
  .length(7, '口座番号は7桁で入力してください。')
  .matches(halfWidthNumber, '口座番号は半角数字で入力してください。')
  .required();

/** 口座名義 */
export const bankAccountHolderSchema = string()
  .label('口座名義')
  .max(30)
  .matches(bankAccountHolder, '口座名義に使用できない文字が含まれています。')
  .required();

/** 精算サイクル */
export const clearingCycleSchema = string()
  .label('精算サイクル');

/** 精算サイクル変更適用年月 */
export const applyFromMonthSchema = string()
  .label('精算サイクル変更適用年月')
  .transform((value) => (value === '' ? undefined : value))
  .notRequired()
  .test(
    'applyFromMonth-validation',
    '精算サイクル変更適用年月は存在する年月を半角数字6桁で、かつ来月以降を指定してください。',
    (value) => {
      if (!value) return true;
      const year = Number(value.slice(0, 4));
      const month = Number(value.slice(4, 6));
      // 半角数字6桁であること
      const isValidFormat = /^\d{6}$/.test(value);
      // 正しい年月であること
      const isValidYearMonth = year >= 1900 && year <= 2100 && month >= 1 && month <= 12;
      if (!isValidFormat || !isValidYearMonth) return false;

      // 来月以降チェック
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;
      const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;
      const nextYear = currentMonth === 12 ? currentYear + 1 : currentYear;

      if (year < nextYear || (year === nextYear && month < nextMonth)) return false;
      return true;
    },
  )
  .test(
    'applyFromMonth-requires-newCycleType',
    '変更予定精算サイクルが未入力の場合、精算サイクル変更適用年月も未入力である必要があります。',
    function validateApplyFromMonthRequiresNewCycleType(value) {
      const { newCycleType } = this.parent;
      const isNewCycleTypeEmpty = !newCycleType || newCycleType === 'UNSELECTED';
      const isApplyFromMonthEmpty = !value;
      return isNewCycleTypeEmpty ? isApplyFromMonthEmpty : true;
    },
  );

/** 変更予定精算サイクル */
export const newCycleTypeSchema = string()
  .label('変更予定精算サイクル')
  .notRequired()
  .test(
    'newCycleType-requires-applyFromMonth',
    '精算サイクル変更適用年月が未入力の場合、変更予定精算サイクルも未入力である必要があります。',
    function validateNewCycleTypeRequiresApplyFromMonth(value) {
      const { applyFromMonth } = this.parent;
      const isApplyFromMonthEmpty = !applyFromMonth;
      const isNewCycleTypeEmpty = !value || value === 'UNSELECTED';
      return isApplyFromMonthEmpty ? isNewCycleTypeEmpty : true;
    },
  );

export const regexSchema = string()
  .label('外部企業ID正規表現');

export const companyCustomField1RegexSchema = string()
  .label('企業自由項目１正規表現');

export const companyCustomField2RegexSchema = string()
  .label('企業自由項目２正規表現');

export const companyCustomField3RegexSchema = string()
  .label('企業自由項目３正規表現');

export const companyCustomField4RegexSchema = string()
  .label('企業自由項目４正規表現');

export const companyCustomField5RegexSchema = string()
  .label('企業自由項目５正規表現');

export const lastUpdatedAtSchema = string()
  .label('最終更新日時')
  .required();

export const bankAccountSchema = object({
  bankCode: bankCodeSchema,
  bankBranchCode: bankBranchCodeSchema,
  bankAccountType: bankAccountTypeSchema,
  bankAccountNumber: bankAccountNumberSchema,
  bankAccountHolder: bankAccountHolderSchema,
})
  .optional()
  .when(
    'clearingCycle',
    ([clearingCycle], schema) => (clearingCycle as ClearingCycleKey === 'NONE' ? schema.default(undefined) : schema.required()),
  );

export const companyEditFormSchema = object({
  externalCompanyId: externalCompanyIdSchema,
  companyName: companyNameSchema,
  nameKana: companyNameKanaSchema,
  nameRomaji: companyNameRomajiSchema,
  merchantCategoryCode: companyMerchantCategoryCodeSchema,
  phone: phoneSchema,
  postalCode: postalCodeSchema,
  prefecture: prefectureSchema,
  municipalities: municipalitiesSchema,
  buildingName: buildingNameSchema,
  startsAt: startsAtSchema,
  endsAt: endsAtSchema,
  customField1: companyCustomField1Schema,
  customField2: companyCustomField2Schema,
  customField3: companyCustomField3Schema,
  customField4: companyCustomField4Schema,
  customField5: companyCustomField5Schema,
  bankAccount: bankAccountSchema,
  clearingCycle: clearingCycleSchema,
  applyFromMonth: applyFromMonthSchema,
  newCycleType: newCycleTypeSchema,
  remarks: remarksSchema,
  regex: regexSchema,
  companyCustomField1Regex: companyCustomField1RegexSchema,
  companyCustomField2Regex: companyCustomField2RegexSchema,
  companyCustomField3Regex: companyCustomField3RegexSchema,
  companyCustomField4Regex: companyCustomField4RegexSchema,
  companyCustomField5Regex: companyCustomField5RegexSchema,
  lastUpdatedAt: lastUpdatedAtSchema,
});

export type CompanyEditForm = InferType<typeof companyEditFormSchema>;
